import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import { I18nProvider } from "@/lib/i18n";
import { CurrencyProvider } from "@/lib/currency";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import VehicleDetail from "@/pages/VehicleDetail";
import Booking from "@/pages/Booking";
import Confirmation from "@/pages/Confirmation";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MyBookings from "@/pages/MyBookings";
import HowItWorks from "@/pages/HowItWorks";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAgencies from "@/pages/admin/AdminAgencies";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AgencyDossier from "@/pages/admin/AgencyDossier";
import AgencyContract from "@/pages/admin/AgencyContract";
import AdminAgencyDetail from "@/pages/admin/AdminAgencyDetail";
import RegisterAgency from "@/pages/RegisterAgency";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminLoginLogs from "@/pages/admin/AdminLoginLogs";
import AdminLodgings from "@/pages/admin/AdminLodgings";
import Legal from "@/pages/Legal";
import LodgingDetail from "@/pages/LodgingDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <I18nProvider>
          <CurrencyProvider>
            <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<Search />} />
            <Route path="/vehicle/:id" element={<VehicleDetail />} />
            <Route path="/booking/:vehicleId" element={<RequireAuth><Booking /></RequireAuth>} />
            <Route path="/booking/lodging/:lodgingId" element={<RequireAuth><Booking /></RequireAuth>} />
            <Route path="/confirmation/:id" element={<RequireAuth><Confirmation /></RequireAuth>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-agency" element={<RegisterAgency />} />
            <Route path="/my-bookings" element={<RequireAuth><MyBookings /></RequireAuth>} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/lodging/:id" element={<LodgingDetail />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/admin" element={<RequireAuth roles={["admin", "agency_partner"]}><AdminLayout /></RequireAuth>}>
              <Route index element={<AdminDashboard />} />
              <Route path="agencies" element={<AdminAgencies />} />
              <Route path="agencies/:id/detail" element={<AdminAgencyDetail />} />
              <Route path="dossier" element={<AgencyDossier />} />
              <Route path="contract" element={<AgencyContract />} />
              <Route path="vehicles" element={<AdminVehicles />} />
              <Route path="lodgings" element={<AdminLodgings />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="login-logs" element={<AdminLoginLogs />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
            </BrowserRouter>
          </CurrencyProvider>
        </I18nProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
